import { Box, Grid, Stack } from "@chakra-ui/react"
import type { ReactNode } from "react"

import { H3 } from "@/components/Typographies"

type Props = React.PropsWithChildren<{
  title: ReactNode
  // Usually VerticalNavTabs: a 300px column from lg, a row of tabs above the content below it.
  // Omitted when the sub-entries live in the sidebar (Network, Sharing, Resources, Settings, Admin).
  nav?: ReactNode
  // Pinned to the bottom of the column from lg, after the tabs below it.
  footer?: ReactNode
}>

// Page of a section: with a secondary navigation (TAdmin), title and tabs in a column beside the
// content from lg, stacked above the content on smaller screens; without one, the title above
// the content, both over the full width. The pages of the sections have their own 32px padding:
// the content gets none (it was doubled), and the title is aligned on those 32px.
export const SubNavLayout: React.FC<Props> = ({ title, nav, footer, children }) => {
  const contentPx = { base: 4, md: 6, lg: 10 }

  return (
    <Grid
      templateColumns={{
        base: "minmax(0, 1fr)",
        lg: nav ? "300px minmax(0, 1fr)" : "minmax(0, 1fr)",
      }}
      templateRows={{
        base: "auto minmax(0, 1fr)",
        lg: nav ? "minmax(0, 1fr)" : "auto minmax(0, 1fr)",
      }}
      backgroundColor="surface"
      h="full"
      data-testid="sub-nav-layout"
    >
      <Stack justifyContent="space-between" spacing={0} minW={0}>
        <Box>
          {/* From lg the band is as high as the seal zone of the sidebar, so the menu entries
              start under the title. */}
          <H3
            display="flex"
            alignItems="flex-end"
            minH={{ lg: "pageHeaderH" }}
            pt={{ base: 6, lg: 0 }}
            pb={{ base: 3, lg: 6 }}
            px={nav ? 6 : { base: 4, md: 8 }}
          >
            {title}
          </H3>
          {nav}
        </Box>
        {footer}
      </Stack>

      <Box p={nav ? contentPx : 0} backgroundColor="surfaceAlt" h="full" minW={0} overflow="auto">
        {children}
      </Box>
    </Grid>
  )
}
